import { useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useListProgressRunsQuery } from '@shared/api';
import type { ProgressRun, ProgressRunStatus } from '@entities/progress';
import {
  decisionLabel,
  decisionTone,
  fmtDateTime,
  isProgressRunActive,
  runObjectLabel,
  statusLabel,
  statusTone,
} from '@entities/progress';
import type { OsnovaTheme } from '@shared/theme/tokens';
import { riseIn } from '@shared/theme/animations';
import { Badge } from '@shared/ui/Badge';
import { Progress } from '@shared/ui/Progress';
import { IconArrowRight } from '@shared/ui/icons';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const ListHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const Muted = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

/** Цвет статус-акцента карточки (левая «корешок»-полоса + точка). */
function statusColor(theme: OsnovaTheme, status: ProgressRunStatus): string {
  switch (status) {
    case 'completed':
      return theme.colors.status.success;
    case 'processing':
      return theme.colors.status.info;
    case 'created':
      return theme.colors.status.warning;
    case 'failed':
      return theme.colors.status.danger;
    default:
      return theme.colors.border;
  }
}

const RunCard = styled.div<{ $status: ProgressRunStatus; $index: number }>`
  position: relative;
  cursor: pointer;
  padding: ${({ theme }) => `${theme.spacing(4)} ${theme.spacing(5)}`};
  padding-left: ${({ theme }) => theme.spacing(6)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2.5)};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  box-shadow: ${({ theme }) => theme.shadow.sm};
  /* Стаггер-вход: fill backwards — чтобы hover-transform не перебивался
     зафиксированным финальным кадром анимации. */
  animation: ${riseIn} 380ms ${({ theme }) => theme.motion.easeOut} backwards;
  animation-delay: ${({ $index }) => `${Math.min($index, 12) * 55}ms`};
  transition:
    transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut},
    box-shadow ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut},
    border-color ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};

  /* Статус-корешок слева. */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    border-radius: ${({ theme }) => `${theme.radius.lg} 0 0 ${theme.radius.lg}`};
    background: ${({ theme, $status }) => statusColor(theme, $status)};
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadow.lg};
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }

  &:focus-visible {
    outline: 2px solid transparent;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const RunTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const RunTitle = styled.div`
  font-weight: 700;
  font-size: 15px;
  letter-spacing: -0.01em;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Badges = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  flex-shrink: 0;
`;

const RunMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  flex-wrap: wrap;
`;

const OpenHint = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-left: auto;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;

  & > svg {
    transition: transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.spring};
  }
  ${RunCard}:hover & > svg {
    transform: translateX(3px);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 14px;
  line-height: 1.55;
  padding: ${({ theme }) => `${theme.spacing(10)} ${theme.spacing(4)}`};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px dashed ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.lg};
`;

/** Заголовок отчёта: краткая сводка прогона либо имя объекта (summary может быть
 *  null до завершения). */
function runTitle(run: ProgressRun): string {
  return run.summary?.trim() || runObjectLabel(run);
}

/** Число показателей в снимке (по завершении). */
function kpiCount(run: ProgressRun): number | null {
  return run.snapshot ? run.snapshot.kpis.length : null;
}

/**
 * Роут `/agents/progress/runs`. Реестр всех сформированных отчётов: статус-акцент,
 * дата, объект, число показателей и решение HITL; клик открывает карточку. Статусы
 * меняются асинхронно — polling, пока есть активные отчёты (created/processing).
 */
export function ProgressRunsList() {
  const navigate = useNavigate();

  const listQ = useListProgressRunsQuery(undefined, { pollingInterval: 4000 });
  const runs = useMemo<ProgressRun[]>(() => listQ.data ?? [], [listQ.data]);
  const hasActive = useMemo(() => runs.some((r) => isProgressRunActive(r.status)), [runs]);

  return (
    <Stack>
      <ListHead>
        <Muted>Отчётов: {runs.length}</Muted>
        {(listQ.isFetching || hasActive) && <Muted>обновление…</Muted>}
      </ListHead>

      {runs.map((r, i) => {
        const count = kpiCount(r);
        return (
          <RunCard
            key={r.id}
            $status={r.status}
            $index={i}
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/agents/progress/runs/${r.id}`)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(`/agents/progress/runs/${r.id}`);
              }
            }}
          >
            <RunTop>
              <RunTitle title={runTitle(r)}>{runTitle(r)}</RunTitle>
              <Badges>
                {r.decision && (
                  <Badge $tone={decisionTone(r.decision)}>{decisionLabel(r.decision)}</Badge>
                )}
                <Badge $tone={statusTone(r.status)}>{statusLabel(r.status)}</Badge>
              </Badges>
            </RunTop>
            {isProgressRunActive(r.status) && <Progress percent={r.progress_percent} />}
            <RunMeta>
              <span>{fmtDateTime(r.created_at)}</span>
              <span>· объект: {runObjectLabel(r)}</span>
              {count !== null && <span>· показателей: {count}</span>}
              <OpenHint>
                Открыть
                <IconArrowRight size={14} />
              </OpenHint>
            </RunMeta>
          </RunCard>
        );
      })}

      {runs.length === 0 && !listQ.isLoading && (
        <EmptyState>
          Пока нет отчётов. Сформируйте первую сводку по объекту — форма запуска на вкладке «Сводные
          отчёты».
        </EmptyState>
      )}
    </Stack>
  );
}
